import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { PaperTagComponent } from '@ricoh-paper/angular';

/**
 * Toggleable chip for allergen tags (UK 14 required allergens).
 *
 * Visual chrome composes <paper-tag intent="error"> when selected — the
 * paper-tag tokens drive the colour palette so the chip re-themes with the
 * rest of the app instead of the hardcoded reds the legacy component used.
 *
 * Usage:
 *   <im-allergen-chip
 *     allergen="peanuts"
 *     [selected]="item.allergens.includes('peanuts')"
 *     (toggle)="onToggleAllergen('peanuts')" />
 *
 * `block` (opt-in) stretches the chip to fill its grid cell with a centred
 * label — used by the dietary preferences page's equal-width allergen grid.
 * Default rendering is unchanged for the catering surfaces.
 */
@Component({
  selector: 'im-allergen-chip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaperTagComponent],
  host: { '[class.ac-host--block]': 'block()' },
  template: `
    <button
      class="ac"
      [class.ac--selected]="selected()"
      type="button"
      [attr.aria-pressed]="selected()"
      (click)="toggle.emit()">
      @if (block()) {
        <span class="ac__pill ac__pill--block" [class.ac__pill--selected]="selected()">{{ allergen() }}</span>
      } @else if (selected()) {
        <paper-tag intent="error">{{ allergen() }}</paper-tag>
      } @else {
        <span class="ac__pill">{{ allergen() }}</span>
      }
    </button>
  `,
  styles: [`
    :host { display: inline-flex; }

    .ac {
      display: inline-flex;
      padding: 0;
      background: transparent;
      border: none;
      cursor: pointer;
      font-family: inherit;
      transition: filter var(--transition, 0.15s ease);

      &:hover { filter: brightness(1.05); }

      &:focus-visible {
        outline: 2px solid var(--color-primary, var(--brand-primary));
        outline-offset: 2px;
        border-radius: var(--radius-pill);
      }
    }

    /* Unselected: outline pill matching paper-tag dimensions but in a quiet
       neutral tone. Hover hints at the destructive selection by tinting
       error-soft. */
    .ac__pill {
      display: inline-flex;
      align-items: center;
      padding: 2px 8px;
      border-radius: var(--radius-pill);
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.06em;
      line-height: 1.3;
      text-transform: uppercase;
      white-space: nowrap;
      background: transparent;
      border: 1px solid var(--border-low);
      color: var(--text-secondary);
      transition: background var(--transition, 0.15s ease),
                  border-color var(--transition, 0.15s ease),
                  color var(--transition, 0.15s ease);

      .ac:hover & {
        background: var(--error-soft);
        border-color: var(--error-strong);
        color: var(--error-strong);
      }
    }

    /* Block mode — equal-width grid cells with a centred label. Selected
       state reuses the same error tokens paper-tag's error intent binds
       to, so the two renderings stay colour-identical. */
    :host(.ac-host--block) {
      display: flex;
      width: 100%;

      .ac { width: 100%; }
    }

    .ac__pill--block {
      width: 100%;
      justify-content: center;
      padding: 5px 8px;
    }

    .ac__pill--selected {
      background: var(--error-soft);
      border-color: var(--error-strong);
      color: var(--error-strong);

      .ac:hover & { filter: brightness(1.02); }
    }

    @media (prefers-reduced-motion: reduce) {
      .ac, .ac__pill { transition: none; }
    }
  `],
})
export class AllergenChipComponent {
  readonly allergen = input.required<string>();
  readonly selected = input(false);
  /** Stretch to fill the parent grid cell (dietary preferences grid). */
  readonly block = input(false);
  readonly toggle = output<void>();
}
